import type { AxiosError } from 'axios';
import { useCallback, useMemo, useState } from 'react';
import { ApiClient } from '~/api/Endpoints';
import useRoute from '~/hooks/useRoute';
import { useUser } from '~/hooks/useUser';
import { isEmail } from '~/scripts/isEmail';

export const useFeedback = (email: string) => {
  const user = useUser();
  const [feedback, setFeedback] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | AxiosError | null>(null);
  const [success, setSuccess] = useState(false);
  const isValidEmail = useMemo(() => isEmail(email), [email]);

  const disabled = useMemo(
    () => !email || !isValidEmail || feedback.length < 10,
    [email, feedback, isValidEmail]
  );

  const route = useRoute();

  const reset = useCallback(() => {
    setFeedback('');
    setError(null);
    setSuccess(false);
    setLoading(false);
  }, []);

  const submit = useCallback(
    async (feedback: string) => {
      if (disabled) {
        return;
      }
      setError(null);
      setSuccess(false);
      setLoading(true);
      const message: string[] = [];
      message.push(`[feedback]`);
      message.push(`[uuid:${user.uuid}]`);
      message.push(`[url:${route.href}]`);
      message.push(`${feedback.replace(/^\s+|\s+$/g, '')}`);
      const api = new ApiClient();
      await api
        .post('/app/leads', {
          body: {
            email,
            query: message.join('\n'),
          },
        })
        .then(() => {
          setSuccess(true);
        })
        .catch((error) => {
          setError(error.message);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [email, user, route, disabled]
  );

  return {
    feedback,
    setFeedback,
    loading,
    success,
    error,
    submit,
    disabled,
    reset,
  };
};
